import { Command } from 'commander';
import { WorkflowManager } from '../core/workflow-manager.js';
import { ProjectConfigManager } from '../core/project-config.js';
import { logger } from '../utils/logger.js';
import { formatDate } from '../utils/date.js';

export function createProjectCommand(): Command {
  const command = new Command('project');
  
  command
    .alias('proj')
    .description('Manage project configuration and information');

  // Show project info
  command
    .command('info')
    .description('Show current project information')
    .action(async () => {
      try {
        const manager = new WorkflowManager();
        
        if (!await manager.isInitialized()) {
          logger.error('Workflow not initialized. Run "workflow init" first.');
          process.exit(1);
        }

        const projectConfig = new ProjectConfigManager(manager.workflowDir);
        const config = await projectConfig.get();

        if (!config) {
          logger.error('Project configuration not found.');
          process.exit(1);
        }

        logger.info('Project Information:');
        logger.info('─'.repeat(50));
        logger.info(`ID:          ${config.project.id}`);
        logger.info(`Name:        ${config.project.name}`);
        if (config.project.description) {
          logger.info(`Description: ${config.project.description}`);
        }
        logger.info(`Created:     ${formatDate(new Date(config.project.created))}`);
        logger.info(`Updated:     ${formatDate(new Date(config.project.updated))}`);
        logger.info(`Version:     ${config.version}`);
        
        if (config.settings && Object.keys(config.settings).length > 0) {
          logger.info('');
          logger.info('Settings:');
          Object.entries(config.settings).forEach(([key, value]) => {
            logger.info(`  ${key}: ${JSON.stringify(value)}`);
          });
        }
      } catch (error: any) {
        logger.error('Failed to get project info:', error.message);
        process.exit(1);
      }
    });

  // Update project info
  command
    .command('update')
    .description('Update project information')
    .option('--name <name>', 'Update project name')
    .option('--desc <desc>', 'Update project description')
    .action(async (options) => {
      try {
        const manager = new WorkflowManager();
        
        if (!await manager.isInitialized()) {
          logger.error('Workflow not initialized. Run "workflow init" first.');
          process.exit(1);
        }

        const projectConfig = new ProjectConfigManager(manager.workflowDir);
        
        const updates: any = {};
        if (options.name) updates.name = options.name;
        if (options.desc) updates.description = options.desc;
        
        if (Object.keys(updates).length === 0) {
          logger.error('No updates specified. Use --name or --desc options.');
          process.exit(1);
        }

        const config = await projectConfig.update(updates);
        logger.success('Project information updated successfully!');
        
        logger.info('');
        logger.info(`Name: ${config.project.name}`);
        if (config.project.description) {
          logger.info(`Description: ${config.project.description}`);
        }
      } catch (error: any) {
        logger.error('Failed to update project info:', error.message);
        process.exit(1);
      }
    });

  // Set project setting
  command
    .command('set <key> <value>')
    .description('Set a project setting')
    .action(async (key: string, value: string) => {
      try {
        const manager = new WorkflowManager();
        
        if (!await manager.isInitialized()) {
          logger.error('Workflow not initialized. Run "workflow init" first.');
          process.exit(1);
        }

        const projectConfig = new ProjectConfigManager(manager.workflowDir);
        
        // Try to parse value as JSON
        let parsedValue: any = value;
        try {
          parsedValue = JSON.parse(value);
        } catch {
          // Keep as string if not valid JSON
        }

        await projectConfig.updateSettings({ [key]: parsedValue });
        logger.success(`Setting "${key}" updated successfully!`);
      } catch (error: any) {
        logger.error('Failed to update setting:', error.message);
        process.exit(1);
      }
    });

  return command;
}